<script>
export default {
  name: 'RightSheet',
  props: {
    show: Boolean,
    close: {
      type: Function,
      default: null,
    },
    submitLabel: {
      type: String,
      required: true,
    },
    dismissLabel: {
      type: [String, Function],
      required: true,
    },
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
    submitLoading: {
      type: Boolean,
    },
  },
  watch: {
    show(value) {
      if (value) {
        document.body.classList.add('no-scroll');
        this.preventBackgroundScroll();
      } else {
        document.body.classList.remove('no-scroll');
        this.allowBackgroundScroll();
      }
    },
  },
  beforeUnmount() {
    document.body.classList.remove('no-scroll');
    this.allowBackgroundScroll();
  },
  methods: {
    preventBackgroundScroll() {
      document.addEventListener('wheel', this.disableScroll, {
        passive: false,
      });
      document.addEventListener('touchmove', this.disableScroll, {
        passive: false,
      });
    },
    allowBackgroundScroll() {
      document.removeEventListener('wheel', this.disableScroll);
      document.removeEventListener('touchmove', this.disableScroll);
    },
    disableScroll(event) {
      // Allow scrolling only inside the modal
      if (!this.$el.contains(event.target)) {
        event.preventDefault();
      }
    },
  },
};
</script>

<template>
  <div class="sheet-wrapper">
    <Transition name="fade">
      <div v-if="show" class="sheet-overlay"></div>
    </Transition>

    <Transition name="slide">
      <div v-if="show" class="sheet-container">
        <div class="sheet-header">
          <div class="sheet-header-content s-text-left">
            <h2 class="sheet-title s-text-xl s-font-semibold">
              {{ title }}
            </h2>
            <p
              v-if="subtitle"
              class="sheet-subtitle s-text-sm s-text-neutral-400 s-leading-snug"
            >
              {{ subtitle }}
            </p>
          </div>
        </div>
        <div class="sheet-body-wrapper">
          <div class="sheet-body">
            <slot name="body">
              default body
            </slot>
          </div>
        </div>
        <div class="sheet-footer s-px-3 s-border-t-gray-200 s-border-t" style="border-top-style: solid">
          <s-button
            @click="() => $emit('dismiss')"
            size="large"
            variant="outline"
            class="close"
            v-if="dismissLabel"
          >
            {{ dismissLabel }}
          </s-button>
          <s-button
            @click="() => $emit('submit')"
            size="large"
            variant="primary"
            class="close"
            :loading="submitLoading"
            v-if="submitLabel"
          >
            {{ submitLabel }}
          </s-button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style lang="scss">
.sheet-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1000;
}

.sheet-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  pointer-events: auto;
  z-index: 1000;
}

.sheet-container {
  position: fixed;
  top: 0;
  right: 0;
  width: 45%;
  min-width: 620px;
  height: 100%;
  background-color: white;
  display: flex;
  flex-direction: column;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
  pointer-events: auto;
  z-index: 1001;
}

.sheet-header {
  padding: 1rem 2rem;
  border-bottom: 1px solid #eaeaea;
  position: relative;
  z-index: 3;
  background-color: white;
}

.sheet-header-content {
  max-width: 100%;
}

.sheet-title {
  margin: 0 0 4px;
}

.sheet-subtitle {
  margin: 0;
}

.sheet-body-wrapper {
  flex-grow: 1;
  position: relative;
  overflow: hidden;
}

.sheet-body {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
}

.sheet-footer {
  padding-top: 20px;
  padding-bottom: 20px;
  position: relative;
  width: 100%;
  background: white;
  text-align: right;
  z-index: 2;
}

/* Fade transition for overlay */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Slide transition for sheet */
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

.slide-enter-to,
.slide-leave-from {
  transform: translateX(0);
}
</style>
